import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { Skeleton } from 'antd'
import { getCubeAPI } from '../../../../../../Cube'
import { QueryRenderer } from '@cubejs-client/react'
import { ReportFilterStore } from '../../ReportFilterStore'
import { parseAndDownloadCSV, GrPrice } from '../../../../../../utilities/helpers'
import { Track } from '../../../../../../Track'
import moment from 'moment'

interface Props {
  companyId: number
  reportFilterStore?: ReportFilterStore
}

interface IOrder {
  field: string
  direction: 'asc' | 'desc'
}

interface State {
  order: IOrder
  fullQuery: any
}

@inject('reportFilterStore')
@observer
export class Products extends React.Component<Props, State> {
  data: any

  constructor(props: Props) {
    super(props)
    this.state = {
      order: {
        field: 'InvoiceLines.total',
        direction: 'desc',
      },
      fullQuery: undefined,
    }
  }

  componentDidMount = () => this.setQuery()

  setQuery = () => {
    const reportFilterStore = this.props.reportFilterStore!

    const fullQuery = {
      measures: ['InvoiceLines.quantity', 'InvoiceLines.quantityreceived', 'InvoiceLines.total'],
      dimensions: ['Products.sku', 'Products.fullname', 'Categories.fullname', 'Suppliers.name'],
      timeDimensions: [
        {
          dimension: 'Invoices.completedate',
          granularity: null,
          dateRange: [reportFilterStore.startDateString, reportFilterStore.endDateString],
        },
      ],
      filters: [
        {
          dimension: 'Invoices.status',
          operator: 'equals',
          values: ['completed'],
        },
        {
          dimension: 'Invoices.locationId',
          operator: 'equals',
          values: reportFilterStore.selectedLocationIds.map((id) => `${id}`),
        },
      ],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      order: {
        [this.state.order.field]: this.state.order.direction,
      },
    }

    this.setState({ fullQuery })
  }

  toggleOrder(fieldName: string) {
    if (this.state.order.field === fieldName) {
      const currentDirection = this.state.order.direction
      this.setState({
        order: {
          field: fieldName,
          direction: currentDirection === 'asc' ? 'desc' : 'asc',
        },
      })
    } else {
      this.setState({
        order: {
          field: fieldName,
          direction: 'desc',
        },
      })
    }

    this.setQuery()
  }

  tableRenderer = ({ resultSet }: any) => {
    const rawData: any[] = resultSet.tablePivot()
    if (rawData.length > 0) {
      return (
        <div className='table-responsive'>
          <table className='table table-sm'>
            <thead>
              <tr>
                <th>
                  <a onClick={() => this.toggleOrder('Products.sku')}>SKU</a>
                </th>
                <th>
                  <a onClick={() => this.toggleOrder('Products.fullname')}>Product</a>
                </th>
                <th>
                  <a onClick={() => this.toggleOrder('Categories.fullname')}>Category</a>
                </th>
                <th>
                  <a onClick={() => this.toggleOrder('Suppliers.name')}>Vendor</a>
                </th>
                <th className='text-right'>
                  <a onClick={() => this.toggleOrder('InvoiceLines.quantity')}>Ordered</a>
                </th>
                <th className='text-right'>
                  <a onClick={() => this.toggleOrder('InvoiceLines.quantityreceived')}>Received</a>
                </th>
                <th className='text-right'>
                  <a onClick={() => this.toggleOrder('InvoiceLines.total')}>Total</a>
                </th>
              </tr>
            </thead>
            <tbody>
              {rawData.map((data, index) => {
                return (
                  <tr key={index}>
                    <td>{data['Products.sku']}</td>
                    <td>{data['Products.fullname']}</td>
                    <td>{data['Categories.fullname']}</td>
                    <td>{data['Suppliers.name']}</td>
                    <td className='text-right'>{data['InvoiceLines.quantity']}</td>
                    <td className='text-right'>{data['InvoiceLines.quantityreceived']}</td>
                    <td className='text-right'>
                      {GrPrice.convertDollarToDollar(data['InvoiceLines.total'])}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    } else {
      return <div>No data</div>
    }
  }

  renderChart(Component: any) {
    const { startDate, endDate } = this.props.reportFilterStore!
    return ({ resultSet, error }: any) => {
      if (resultSet) {
        Track.track('dashboard:reports_purchaseOrderProducts', {
          numberOfDays: moment(endDate).diff(startDate, 'days'),
        })
        this.data = resultSet.rawData()
      } else {
        Track.startTimer('dashboard:reports_purchaseOrderProducts')
        this.data = undefined
      }
      return (
        (resultSet && <Component resultSet={resultSet} />) ||
        (error && error.toString()) || <Skeleton active />
      )
    }
  }

  exportCSV() {
    const { startDate, endDate } = this.props.reportFilterStore!
    const data = this.data
    if (data) {
      parseAndDownloadCSV(`purchase-order-products-${startDate}-${endDate}`, data)
    }
  }

  render() {
    const { fullQuery } = this.state
    const { startDateString, endDateString } = this.props.reportFilterStore!

    return (
      <>
        <div className='rounded background-white'>
          <div className='p-3'>
            <div>
              <b>Products</b>
            </div>
            <div className='text-muted'>Not including taxes nor fees</div>

            <div className='mt-2 mb-2'>
              <button className='btn btn-light btn-sm' onClick={() => this.exportCSV()}>
                Export as csv
              </button>
            </div>

            <div className='mt-2'>
              {fullQuery ? (
                <QueryRenderer
                  query={fullQuery}
                  cubejsApi={getCubeAPI(this.props.companyId)}
                  render={this.renderChart(this.tableRenderer)}
                />
              ) : (
                <Skeleton active />
              )}
            </div>
          </div>
        </div>
      </>
    )
  }
}
